import { CSSProperties, useRef, useState } from "react";
import { CardShift, Direction } from "../interface/game";
import { Card, CardData } from "./Card";
import './Pile.css'
import { useGameContext, usePile } from "../context/game-context";
import { listΑfter } from "../model/util/list";

export type CardDisplayProperties = {
    id: string;
    card: CardData;
    shift: CardShift
    draggable: boolean;
};

export type PileData = Array<CardDisplayProperties>;

export interface PileDragState {
    pileId: string;
    cards: PileData;
    direction: Direction;
    width: number;
    height: number;
    xOff: number;
    yOff: number;
    xPos?: number;
    yPos?: number;
    onDrop(destPileId: string): void;
    onRelease(): void;
}

export interface PileDataProperties {
    pileId: string;
    direction: Direction;
    clickable: boolean;
}

export interface PileProperties extends PileDataProperties {
    onPileDrag(handler: PileDragState): void;
    onPileDragMove: (xpos: number, ypos: number) => void;
    onPileEnter(pileId: string): void;
    onPileLeave(pileId: string): void;
}

export interface PileState {
    pileId: string;
    direction: Direction;
}

export const pileCardMargins: {[key: string]: [string, number, number, number]} = {
    east: [ 'left', 15, 2, 0],
    west: [ 'left', -15, -2, 0],
    north: [ 'top', -15, -2, 0],
    south: [ 'top', 15, 2, 0],
}

export function Pile({
    pileId, 
    direction, 
    clickable, 
    onPileDrag,
    onPileDragMove,
    onPileEnter, 
    onPileLeave
}: PileProperties) {
    const pileRef = useRef<HTMLDivElement>(null);
    const pile = usePile(pileId);
    const context = useGameContext();
    const [draggingCard, setDraggingCard] = useState<string>();
    
    // const [displayPile, hiddenPile]: PileData[] = listBeforeAndΑfter(pile, (card) => card.id === draggingCard);
    const hideIndex = pile.findIndex((card) => card.id === draggingCard);
    

    const marginSpec = pileCardMargins[direction]; 

    function handleCardDrag(cardId: string, xOff: number, yOff: number, xPos?: number, yPos?: number) {
        setDraggingCard(cardId);
        const draggingPile: PileData = listΑfter(pile, (card) => card.id === cardId);

        onPileDrag({
            pileId: pileId,
            cards: draggingPile,
            direction: direction,
            width: pileRef.current?.offsetWidth ?? 10,
            height: pileRef.current?.offsetHeight ?? 10,
            xOff: xOff,
            yOff: yOff,
            xPos: xPos,
            yPos: yPos,
            onDrop(destPileId: string) {
                setDraggingCard(undefined);
                context.dropPile(destPileId, pileId, draggingPile[0].id);
            },
            onRelease() {
                setDraggingCard(undefined);
            }
        });
    }

    function mouseEnter() {
        console.log(`Enter pile ${pileId}`);
        onPileEnter(pileId);
    }

    function mouseLeave() {
        console.log(`Leave pile ${pileId}`);
        onPileLeave(pileId);
    }

    function handleClick() {
        if (clickable) {
            context.clickPile(pileId);
        }
    }

    return <div 
        ref={pileRef} 
        className={`card-pile card-pile-${direction}` + ((pile.length === 0 || hideIndex === 0) ? ' card-pile-empty' : '')} 
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseLeave}
        onClick={handleClick}
    >
        {pile.map(function cardMapper(this: {offset: number}, cardData, index) {
            const cardStyle: CSSProperties = {
                [marginSpec[0]]: `${this.offset}%`,
                display: (hideIndex >= 0 && index >= hideIndex) ? 'none' : 'block'
            }
            this.offset += marginSpec[cardData.shift+1] as number;
            return <Card 
                className={`card ${cardData.shift === CardShift.OPEN ? 'open' : ''}`}
                {...cardData.card}
                cardId={cardData.id}
                draggable={cardData.draggable}
                onCardDrag={handleCardDrag}
                onCardDragMove={onPileDragMove}
                key={cardData.id}
                style={cardStyle} />
        }, {offset: 0})}
    </div>
}